import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ConfirmActionModalData, ModalActionButton } from './confirm-action-modal.model';

@Component({
  selector: 'app-confirm-action-modal',
  standalone: false,
  templateUrl: './confirm-action-modal.component.html',
  styleUrls: ['./confirm-action-modal.component.scss'],
})
export class ConfirmActionModalComponent implements OnChanges {
  @Input({ required: true }) data!: ConfirmActionModalData;
  @Input() isOpen = false;

  /** Emits the `action` string of whichever non-cancel button was clicked */
  @Output() actionTriggered = new EventEmitter<string>();
  /** Emitted on Cancel or backdrop/Escape */
  @Output() dismissed = new EventEmitter<void>();

  confirmationValue = '';
  reasonValue = '';
  parsedDescription: SafeHtml = '';
  parsedTitle: SafeHtml = '';
  parsedEffects: SafeHtml[] = [];

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data) {
      this.parsedDescription = this.parseDescription(this.data.description);
      this.parsedTitle = this.parseTitle(this.data);
      this.parsedEffects = (this.data.effects ?? []).map(e => this.parseBold(e.text));
    }
    if (changes['isOpen'] && this.isOpen) {
      this.confirmationValue = '';
      this.reasonValue = '';
    }
  }

  get isConfirmed(): boolean {
    if (this.data?.reasonInput?.required && !this.reasonValue.trim()) return false;
    if (!this.data?.confirmation) return true;
    return this.confirmationValue === this.data.confirmation.expectedValue;
  }

  isButtonDisabled(action: ModalActionButton): boolean {
    return !!action.disabledUntilConfirmed && !this.isConfirmed;
  }

  onActionClick(action: ModalActionButton): void {
    if (this.isButtonDisabled(action)) return;
    this.actionTriggered.emit(action.action);
  }

  onDismiss(): void {
    this.dismissed.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('cam-backdrop')) {
      this.onDismiss();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen) {
      this.onDismiss();
    }
  }

  private parseDescription(text: string): SafeHtml {
    return this.parseBold(text);
  }

  private parseBold(text: string): SafeHtml {
    if (!text) return '';
    const html = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private parseTitle(data: ConfirmActionModalData): SafeHtml {
    if (!data?.title) return '';
    const cls = data.titleHighlight === 'danger' ? 'cam-title-danger'
              : data.titleHighlight === 'success' ? 'cam-title-success' : '';
    const html = data.title.replace(/\*\*(.+?)\*\*/g,
      cls ? `<strong class="${cls}">$1</strong>` : '<strong>$1</strong>');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
